import { useRef } from 'react';
import { useMountEffect, useStore } from '../lib/hooks';
import { formatTimecode } from '../lib/format';
import { Corners } from './ui';
import type { ScreenRecorder } from '../lib/engine';
import type { ClipPlayer } from '../lib/player';

/** Mounts the engine's live surface: capture video, or the compositor canvas. */
function LiveSurface({ engine }: { engine: ScreenRecorder }) {
  const host = useRef<HTMLDivElement | null>(null);
  useMountEffect(() => {
    const node = host.current;
    if (!node) return;
    return engine.attachMonitor(node);
  });
  return <div className="stage__host" ref={host} />;
}

function PreviewSurface({ player }: { player: ClipPlayer }) {
  const host = useRef<HTMLDivElement | null>(null);
  useMountEffect(() => {
    const node = host.current;
    const element = player.element;
    if (!node) return;
    element.classList.add('monitor-surface--preview');
    node.appendChild(element);
    return () => {
      element.classList.remove('monitor-surface--preview');
      if (element.parentElement === node) node.removeChild(element);
    };
  });
  return <div className="stage__host" ref={host} />;
}

function StageTimecode({ player }: { player: ClipPlayer }) {
  const playerState = useStore(player.store);
  return <div className="stage__timecode">{formatTimecode(playerState.currentUs)}</div>;
}

export type StageMode = 'idle' | 'live' | 'preview' | 'unavailable';

export function Stage({
  engine,
  player,
  mode,
  armed,
  countdown,
}: {
  engine: ScreenRecorder;
  player: ClipPlayer | null;
  mode: StageMode;
  /** True once the capture surface exists, so the live monitor can be mounted. */
  armed: boolean;
  countdown: number | null;
}) {
  return (
    <div className="stage">
      <div className="stage__frame">
        <Corners />
        <div className="stage__grid" aria-hidden="true" />
        {mode === 'live' && armed ? <LiveSurface engine={engine} /> : null}
        {mode === 'preview' && player ? <PreviewSurface key={player.id} player={player} /> : null}
        {mode === 'idle' ? (
          <div className="stage__placeholder">
            <span className="reticle" aria-hidden="true" />
            <p className="stage__placeholder-title">No signal</p>
            <p className="stage__placeholder-copy">
              Choose a source, arm the recorder, and the take lands here for trimming. Nothing
              leaves this machine.
            </p>
          </div>
        ) : null}
        {mode === 'unavailable' ? (
          <div className="stage__placeholder">
            <span className="reticle" aria-hidden="true" />
            <p className="stage__placeholder-title">Preview skipped</p>
            <p className="stage__placeholder-copy">
              This take is long enough that keeping a playable copy in memory would be wasteful, so
              scrubbing is off. Exporting still works — set the range from the readouts, or trim the
              file after the fact.
            </p>
          </div>
        ) : null}
        {mode === 'live' ? (
          <div className={`stage__badge${countdown !== null ? ' is-armed' : ''}`}>
            <span className="rec-dot" aria-hidden="true" />
            {countdown !== null ? 'ARMED' : 'REC'}
          </div>
        ) : null}
        {mode === 'preview' && player ? <StageTimecode player={player} /> : null}
        {countdown !== null ? (
          <div className="countdown" aria-live="polite">
            <span className="countdown__ring" />
            <span className="countdown__value">{Math.max(1, Math.ceil(countdown / 1000))}</span>
          </div>
        ) : null}
      </div>
    </div>
  );
}
